import type { QuestionResult } from './useSurveyResults';
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { config } from 'config';
import { RESULTS_STALE_MS, resultsKey } from './useSurveyResults';

export type BreakdownExperiment = { id: number; name: string; status: string };

export type VariantBreakdown = {
  variantId: string;
  name: string;
  isControl: boolean;
  impressions: number;
  respondents: number;
  responseRate: number | null;
  lowSample: boolean;
  questions: QuestionResult[];
};

export type SurveyVariantBreakdown = {
  experimentId: number;
  experimentName: string;
  lowSample: number;
  variants: VariantBreakdown[];
};

/**
 * The experiments list scans every impression the survey has had for the keys of
 * its variant maps, and rarely changes, so it stays fresh for five minutes.
 */
const EXPERIMENTS_STALE_MS = 5 * 60_000;

type SurveyVariantBreakdownParams = {
  surveyId: number;
  /** False until the panel is open, so a survey nobody splits never runs the breakdown. */
  enabled: boolean;
};

/**
 * The per-variant breakdown for one experiment. The choice starts on the
 * experiment the survey is tied to, which the server lists first; with no link it
 * starts on the first experiment the survey's visitors were in.
 */
export const useSurveyVariantBreakdown = ({ surveyId, enabled }: SurveyVariantBreakdownParams) => {
  const statsClient = useClient(config.STATISTICS_URL);
  const [chosen, setChosen] = useState<number | null>(null);

  const {
    data: experiments = [],
    isLoading: isListLoading,
    isError: isListError,
  } = useQuery<BreakdownExperiment[]>({
    enabled: enabled && !!surveyId,
    queryKey: resultsKey(surveyId, 'breakdown-experiments'),
    queryFn: () => statsClient.get<BreakdownExperiment[]>(`/surveys/${surveyId}/breakdown/experiments`),
    staleTime: EXPERIMENTS_STALE_MS,
  });

  // Derived, not synced into state: the default follows the list until someone
  // picks, and a pick that has since dropped out of the list falls back rather
  // than leaving the select showing one experiment while another is queried.
  const experimentId = experiments.some((experiment) => experiment.id === chosen)
    ? chosen
    : (experiments[0]?.id ?? null);

  const {
    data: breakdown,
    isLoading,
    isError,
  } = useQuery<SurveyVariantBreakdown>({
    enabled: enabled && !!surveyId && experimentId !== null,
    queryKey: resultsKey(surveyId, 'breakdown', experimentId),
    queryFn: () =>
      statsClient.get<SurveyVariantBreakdown>(`/surveys/${surveyId}/breakdown?experimentId=${experimentId}`),
    staleTime: RESULTS_STALE_MS,
    // The table on screen stays while another experiment's loads.
    placeholderData: keepPreviousData,
  });

  return {
    experiments,
    experimentId,
    setExperimentId: setChosen,
    breakdown,
    isListLoading,
    isLoading: isListLoading || isLoading,
    isError: isListError || isError,
  };
};

export type SurveyVariantBreakdownManager = ReturnType<typeof useSurveyVariantBreakdown>;
